/**
 * FileList — the file dialogs' folder-and-file listing, as a list (name, type,
 * size, modified, source arche) or a grid of thumbnails.
 *
 * One keyboard model for both views, as a roving-tabindex listbox: the list is
 * a single Tab stop, and inside it
 *   ↑ ↓ (and ← → in the grid)  move        Home / End  first / last
 *   Enter                      open a folder, or choose a file
 *   Space                      select a file (toggle, when several may be chosen)
 *   Backspace / Alt+↑          up one folder
 *   typing                     jumps to the next name starting with what was typed
 * Reaching the end of what has loaded fetches the next page.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronRight, Folder, Loader2 } from 'lucide-react';
import type { VaultDocumentFolderDto, VaultFileDialogDocumentDto } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { formatBytes, formatModified, iconForMime, typeLabel } from './format';
import { describeFolderPath } from './locations';
import type { FileDialogView } from './prefs';

export type FileListEntry =
  | { kind: 'folder'; id: string; name: string; folder: VaultDocumentFolderDto }
  | {
      kind: 'file';
      id: string;
      name: string;
      file: VaultFileDialogDocumentDto;
      /** Why it cannot be chosen here ("Not a type this arche opens"), or null. */
      disabledReason: string | null;
    };

export interface FileListProps {
  entries: readonly FileListEntry[];
  view: FileDialogView;
  /** Show each file's folder (search and Recent span folders). */
  showLocation: boolean;
  selectedIds: ReadonlySet<string>;
  multiSelect: boolean;
  thumbnails: ReadonlyMap<string, string>;
  /** Accessible name of the listbox ("Files in Documents / Projects"). */
  label: string;
  onOpenFolder: (folder: VaultDocumentFolderDto) => void;
  onSelectFile: (file: VaultFileDialogDocumentDto, opts: { toggle: boolean }) => void;
  onActivateFile: (file: VaultFileDialogDocumentDto) => void;
  /** The entry with keyboard focus changed (drives the preview pane). */
  onFocusEntry?: (entry: FileListEntry | null) => void;
  onUp: () => void;
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
}

const TYPEAHEAD_RESET_MS = 700;
const GRID_TILE_PX = 136;

export function FileList(props: FileListProps) {
  const { entries, view, selectedIds, onFocusEntry, hasMore, isLoadingMore, onLoadMore } = props;
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLDivElement | null>>([]);
  /** Move DOM focus with the active index only after a keyboard move — a
   *  re-render (a page loading in) must never pull focus into the list. */
  const focusOnMove = useRef(false);
  const typeahead = useRef<{ text: string; at: number }>({ text: '', at: 0 });
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Keep the active index inside the list as it changes (navigation, filters).
  useEffect(() => {
    setActiveIndex((i) => Math.min(i, Math.max(0, entries.length - 1)));
  }, [entries.length]);

  // A new folder or query starts at the top.
  const firstId = entries[0]?.id;
  useEffect(() => {
    setActiveIndex(0);
  }, [firstId]);

  useEffect(() => {
    if (!focusOnMove.current) return;
    focusOnMove.current = false;
    const el = itemRefs.current[activeIndex];
    el?.focus();
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex]);

  useEffect(() => {
    onFocusEntry?.(entries[activeIndex] ?? null);
  }, [activeIndex, entries, onFocusEntry]);

  // Infinite scroll: the sentinel coming into view loads the next page. The
  // "Load more" button below is the fallback (and the keyboard path).
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver((records) => {
      if (records.some((r) => r.isIntersecting)) onLoadMore();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, onLoadMore, entries.length]);

  const columns = useCallback(() => {
    if (view !== 'grid') return 1;
    const width = containerRef.current?.clientWidth ?? 0;
    return Math.max(1, Math.floor(width / GRID_TILE_PX));
  }, [view]);

  const moveTo = useCallback(
    (index: number) => {
      if (entries.length === 0) return;
      const next = Math.max(0, Math.min(entries.length - 1, index));
      focusOnMove.current = true;
      setActiveIndex(next);
      if (next >= entries.length - 1 && hasMore) onLoadMore();
    },
    [entries.length, hasMore, onLoadMore],
  );

  const activate = (entry: FileListEntry | undefined) => {
    if (!entry) return;
    if (entry.kind === 'folder') props.onOpenFolder(entry.folder);
    else if (!entry.disabledReason) props.onActivateFile(entry.file);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const cols = columns();
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        moveTo(activeIndex + cols);
        return;
      case 'ArrowUp':
        e.preventDefault();
        if (e.altKey) props.onUp();
        else moveTo(activeIndex - cols);
        return;
      case 'ArrowRight':
        if (view === 'grid') {
          e.preventDefault();
          moveTo(activeIndex + 1);
        }
        return;
      case 'ArrowLeft':
        if (view === 'grid') {
          e.preventDefault();
          moveTo(activeIndex - 1);
        }
        return;
      case 'Home':
        e.preventDefault();
        moveTo(0);
        return;
      case 'End':
        e.preventDefault();
        moveTo(entries.length - 1);
        return;
      case 'Enter':
        e.preventDefault();
        activate(entries[activeIndex]);
        return;
      case ' ': {
        const entry = entries[activeIndex];
        if (entry?.kind === 'file' && !entry.disabledReason) {
          e.preventDefault();
          props.onSelectFile(entry.file, { toggle: props.multiSelect });
        }
        return;
      }
      case 'Backspace':
        e.preventDefault();
        props.onUp();
        return;
      default:
        break;
    }
    // Typeahead: printable characters, no modifier.
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      const now = Date.now();
      const state = typeahead.current;
      state.text = now - state.at > TYPEAHEAD_RESET_MS ? e.key : state.text + e.key;
      state.at = now;
      // Repeating one letter ("b", "b", "b") cycles through names starting
      // with it; anything else is a prefix to match from the current item.
      const text = state.text.toLowerCase();
      const cycling = [...text].every((c) => c === text[0]);
      const needle = cycling ? text[0] : text;
      const startAt = cycling ? activeIndex + 1 : activeIndex;
      for (let step = 0; step < entries.length; step += 1) {
        const index = (startAt + step) % entries.length;
        if (entries[index].name.toLowerCase().startsWith(needle)) {
          moveTo(index);
          break;
        }
      }
    }
  };

  const optionClass = (entry: FileListEntry, index: number) =>
    cn(
      'outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
      entry.kind === 'file' && selectedIds.has(entry.id)
        ? 'bg-primary/10 hover:bg-primary/15'
        : 'hover:bg-muted/60',
      entry.kind === 'file' && entry.disabledReason && 'opacity-50',
      index === activeIndex && 'bg-muted/40',
    );

  const renderIcon = (entry: FileListEntry, size: 'sm' | 'lg') => {
    const box = size === 'sm' ? 'h-9 w-9' : 'h-20 w-full';
    const glyph = size === 'sm' ? 'h-5 w-5' : 'h-10 w-10';
    if (entry.kind === 'folder') {
      return (
        <div className={cn(box, 'flex shrink-0 items-center justify-center rounded bg-muted/40')}>
          <Folder className={cn(glyph, 'text-muted-foreground')} aria-hidden="true" />
        </div>
      );
    }
    const thumb = props.thumbnails.get(entry.id);
    const Icon = iconForMime(entry.file.mimeType);
    return (
      <div
        className={cn(
          box,
          'flex shrink-0 items-center justify-center overflow-hidden rounded bg-muted/40',
        )}
      >
        {thumb ? (
          <img src={thumb} alt="" className="h-full w-full object-cover" loading="lazy" />
        ) : (
          <Icon className={cn(glyph, 'text-muted-foreground')} aria-hidden="true" />
        )}
      </div>
    );
  };

  const secondary = (entry: FileListEntry) => {
    if (entry.kind === 'folder') return 'Folder';
    const f = entry.file;
    const parts = [
      typeLabel(f.name, f.mimeType),
      formatBytes(f.sizeBytes),
      formatModified(f.updatedAt),
    ];
    if (props.showLocation) parts.push(describeFolderPath(f.folderPath));
    return parts.join(' · ');
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div
        ref={containerRef}
        role="listbox"
        aria-label={props.label}
        aria-multiselectable={props.multiSelect || undefined}
        onKeyDown={onKeyDown}
        className={cn(
          'min-h-0 flex-1 overflow-auto',
          view === 'grid'
            ? 'grid auto-rows-min grid-cols-[repeat(auto-fill,minmax(8rem,1fr))] gap-2 p-2'
            : 'divide-y',
        )}
      >
        {entries.map((entry, index) => {
          const selected = entry.kind === 'file' && selectedIds.has(entry.id);
          const disabled = entry.kind === 'file' && !!entry.disabledReason;
          return (
            <div
              key={`${entry.kind}:${entry.id}`}
              ref={(el) => {
                itemRefs.current[index] = el;
              }}
              role="option"
              id={`file-dialog-option-${entry.id}`}
              aria-selected={selected}
              aria-disabled={disabled || undefined}
              title={
                entry.kind === 'file' && entry.disabledReason ? entry.disabledReason : undefined
              }
              tabIndex={index === activeIndex ? 0 : -1}
              onFocus={() => setActiveIndex(index)}
              onClick={(e) => {
                setActiveIndex(index);
                if (entry.kind === 'folder') props.onOpenFolder(entry.folder);
                else if (!disabled) {
                  props.onSelectFile(entry.file, {
                    toggle: props.multiSelect || e.ctrlKey || e.metaKey,
                  });
                }
              }}
              onDoubleClick={() => {
                if (entry.kind === 'file' && !disabled) props.onActivateFile(entry.file);
              }}
              className={cn(
                optionClass(entry, index),
                'cursor-pointer select-none',
                view === 'grid'
                  ? 'flex min-h-[44px] flex-col gap-1 rounded-md border p-2'
                  : 'flex min-h-[44px] items-center gap-3 px-3 py-2',
              )}
            >
              {view === 'grid' ? (
                <>
                  {renderIcon(entry, 'lg')}
                  <div className="truncate text-sm font-medium" title={entry.name}>
                    {entry.name}
                  </div>
                  {entry.kind === 'file' && entry.file.sourceArcheName && (
                    <ArcheBadge name={entry.file.sourceArcheName} />
                  )}
                </>
              ) : (
                <>
                  {renderIcon(entry, 'sm')}
                  <div className="min-w-0 flex-1">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="truncate text-sm font-medium" title={entry.name}>
                        {entry.name}
                      </span>
                      {entry.kind === 'file' && entry.file.sourceArcheName && (
                        <ArcheBadge name={entry.file.sourceArcheName} />
                      )}
                    </div>
                    <div className="truncate text-xs text-muted-foreground">
                      {entry.kind === 'file' && entry.disabledReason
                        ? entry.disabledReason
                        : secondary(entry)}
                    </div>
                  </div>
                  {entry.kind === 'folder' && (
                    <ChevronRight
                      className="h-4 w-4 shrink-0 text-muted-foreground"
                      aria-hidden="true"
                    />
                  )}
                </>
              )}
            </div>
          );
        })}
        {hasMore && <div ref={sentinelRef} aria-hidden="true" className="h-1" />}
      </div>
      {hasMore && (
        <div className="flex justify-center border-t p-2">
          <Button variant="ghost" size="sm" onClick={onLoadMore} disabled={isLoadingMore}>
            {isLoadingMore && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
            Load more
          </Button>
        </div>
      )}
    </div>
  );
}

/** The arche that made a file. Display only — never an access decision. */
function ArcheBadge({ name }: { name: string }) {
  return (
    <span
      className="max-w-[10rem] shrink-0 truncate rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground"
      title={`Made by ${name}`}
    >
      {name}
    </span>
  );
}
